/**
 * BIRHAUS v3.0 Migration Utilities
 * Helpers for migrating from v2 to v3 components with backward compatibility
 */

import { ReactNode } from 'react'

// Type definitions for migration
export interface V2ButtonProps {
  label?: string
  labelEs?: string
  labelEn?: string
  variant?: 'primary' | 'secondary' | 'destructive' | 'outline' | 'ghost'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  loading?: boolean
  disabled?: boolean
  fullWidth?: boolean
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  children?: ReactNode
  className?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}

export interface V3ButtonProps extends V2ButtonProps {
  radius?: string
  glow?: boolean
  float?: boolean
  pulse?: boolean
  ripple?: boolean
  glass?: boolean
  trackPerformance?: boolean
}

export interface V2CardProps {
  title?: string
  titleEs?: string
  titleEn?: string
  subtitle?: string
  description?: string
  children?: ReactNode
  header?: ReactNode
  footer?: ReactNode
  variant?: 'default' | 'elevated' | 'outlined' | 'minimal'
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

export interface V3CardProps extends V2CardProps {
  radius?: string
  hoverable?: boolean
  glow?: boolean
  float?: boolean
  glass?: boolean
  frosted?: boolean
  trackPerformance?: boolean
}

/**
 * Migration strategies for different component types
 */
export const migrationStrategies = {
  // Conservative migration - minimal changes
  conservative: {
    button: {
      enableFloat: false,
      enableGlow: false,
      enableGlass: false,
      radiusMultiplier: 1.5, // Modest increase
    },
    card: {
      enableHover: false,
      enableFloat: false,
      enableGlass: false,
      radiusMultiplier: 2, // Moderate increase
    },
  },
  
  // Progressive migration - gradual adoption
  progressive: {
    button: {
      enableFloat: true,
      enableGlow: false,
      enableGlass: false,
      radiusMultiplier: 2.5, // Notable increase
    },
    card: {
      enableHover: true,
      enableFloat: false,
      enableGlass: false,
      radiusMultiplier: 3, // Significant increase
    },
  },
  
  // Radical migration - full v3 experience
  radical: {
    button: {
      enableFloat: true,
      enableGlow: true,
      enableGlass: true,
      radiusMultiplier: 3.5, // Full v3 radius
    },
    card: {
      enableHover: true,
      enableFloat: true,
      enableGlass: true,
      radiusMultiplier: 4, // Full v3 radius
    },
  },
} as const

/**
 * Migrate v2 button props to v3 props
 */
export const migrateButtonProps = (
  oldProps: V2ButtonProps,
  strategy: keyof typeof migrationStrategies = 'progressive'
): V3ButtonProps => {
  const { button } = migrationStrategies[strategy]
  const newProps: V3ButtonProps = { ...oldProps }
  
  // Size migration with v3 enhancements
  if (oldProps.size === 'xs') newProps.size = 'sm' // Bump up minimum size
  
  // Enable v3 features based on strategy
  if (button.enableFloat && oldProps.variant === 'primary') {
    newProps.float = true
  }
  
  if (button.enableGlow && oldProps.variant === 'primary') {
    newProps.glow = true
  }
  
  if (button.enableGlass && oldProps.variant === 'secondary') {
    newProps.variant = 'glass' as any
  }
  
  // Always enable ripple for better feedback
  newProps.ripple = true
  
  // Add performance tracking in development
  if (process.env.NODE_ENV === 'development') {
    newProps.trackPerformance = true
  }
  
  console.log(`[BIRHAUS v3 Migration] Button migrated with ${strategy} strategy`, {
    original: oldProps,
    migrated: newProps
  })
  
  return newProps
}

/**
 * Migrate v2 card props to v3 props
 */
export const migrateCardProps = (
  oldProps: V2CardProps,
  strategy: keyof typeof migrationStrategies = 'progressive'
): V3CardProps => {
  const { card } = migrationStrategies[strategy]
  const newProps: V3CardProps = { ...oldProps }
  
  // Enable hover effects based on strategy
  if (card.enableHover) {
    newProps.hoverable = true
  }
  
  // Enable floating animation for elevated cards
  if (card.enableFloat && oldProps.variant === 'elevated') {
    newProps.float = true
  }
  
  // Apply glass effect to outlined cards
  if (card.enableGlass && oldProps.variant === 'outlined') {
    newProps.variant = 'glass' as any
  }
  
  // Always apply generous padding
  if (oldProps.size === 'sm') newProps.size = 'md'
  
  // Add performance tracking in development
  if (process.env.NODE_ENV === 'development') {
    newProps.trackPerformance = true
  }
  
  console.log(`[BIRHAUS v3 Migration] Card migrated with ${strategy} strategy`, {
    original: oldProps,
    migrated: newProps
  })
  
  return newProps
}

/**
 * Migration wrapper for gradual component adoption
 */
export const withV3Migration = <T extends Record<string, any>>(
  Component: React.ComponentType<T>,
  migrateProps: (props: any, strategy?: keyof typeof migrationStrategies) => T,
  strategy: keyof typeof migrationStrategies = 'progressive'
) => {
  const WrappedComponent = (props: any) => {
    const migratedProps = migrateProps(props, strategy)
    
    // Show migration notice in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`[BIRHAUS v3 Migration] ${Component.displayName || Component.name}`)
      console.log('Strategy:', strategy)
      console.log('Original props:', props)
      console.log('Migrated props:', migratedProps)
      console.groupEnd()
    }
    
    return <Component {...migratedProps} />
  }
  
  WrappedComponent.displayName = `V3Migration(${Component.displayName || Component.name})`
  
  return WrappedComponent
}

/**
 * Automatic props analyzer to suggest migration strategy
 */
export const analyzeMigrationComplexity = (props: Record<string, any>): {
  complexity: 'low' | 'medium' | 'high'
  suggestedStrategy: keyof typeof migrationStrategies
  reasons: string[]
} => {
  const reasons: string[] = []
  let complexity: 'low' | 'medium' | 'high' = 'low'
  
  // Check for complex interactions
  if (props.onClick) {
    reasons.push('Has click handler')
    complexity = 'medium'
  }
  
  if (props.disabled) {
    reasons.push('Has disabled state')
  }
  
  if (props.loading) {
    reasons.push('Has loading state')
    complexity = 'medium'
  }
  
  // Check for custom styling
  if (props.className && props.className.includes('shadow')) {
    reasons.push('Uses custom shadows - may conflict with v3')
    complexity = 'high'
  }
  
  if (props.className && props.className.includes('rounded')) {
    reasons.push('Uses custom border radius - may conflict with v3')
    complexity = 'high'
  }
  
  // Determine suggested strategy
  let suggestedStrategy: keyof typeof migrationStrategies = 'conservative'
  
  if (complexity === 'low') {
    suggestedStrategy = 'radical'
  } else if (complexity === 'medium') {
    suggestedStrategy = 'progressive'
  }
  
  return { complexity, suggestedStrategy, reasons }
}

/**
 * CSS class migration utilities
 */
export const migrateCSSClasses = {
  // Border radius migration
  borderRadius: {
    'rounded-sm': 'rounded-[12px]', // 2px -> 12px
    'rounded': 'rounded-[20px]',    // 4px -> 20px
    'rounded-md': 'rounded-[20px]', // 6px -> 20px
    'rounded-lg': 'rounded-[28px]', // 8px -> 28px
    'rounded-xl': 'rounded-[40px]', // 12px -> 40px
    'rounded-2xl': 'rounded-[56px]', // 16px -> 56px
  },
  
  // Spacing migration (2x increase)
  spacing: {
    'p-2': 'p-4',   // 8px -> 16px
    'p-3': 'p-6',   // 12px -> 24px
    'p-4': 'p-8',   // 16px -> 32px
    'p-6': 'p-12',  // 24px -> 48px
    'p-8': 'p-16',  // 32px -> 64px
    'm-2': 'm-4',   // 8px -> 16px
    'm-3': 'm-6',   // 12px -> 24px
    'm-4': 'm-8',   // 16px -> 32px
    'm-6': 'm-12',  // 24px -> 48px
    'm-8': 'm-16',  // 32px -> 64px
  },
  
  // Shadow migration
  shadows: {
    'shadow-sm': 'shadow-lg',  // Subtle -> Noticeable
    'shadow': 'shadow-xl',     // Default -> Elevated
    'shadow-md': 'shadow-xl',  // Medium -> Elevated
    'shadow-lg': 'shadow-2xl', // Large -> Dramatic
  }
}

/**
 * Migrate CSS classes from v2 to v3
 */
export const migrateClassName = (
  className: string = '',
  options: {
    migrateBorders?: boolean
    migrateSpacing?: boolean
    migrateShadows?: boolean
  } = {}
): string => {
  let migratedClassName = className
  
  const { migrateBorders = true, migrateSpacing = true, migrateShadows = true } = options
  
  // Apply border radius migrations
  if (migrateBorders) {
    Object.entries(migrateCSSClasses.borderRadius).forEach(([oldClass, newClass]) => {
      migratedClassName = migratedClassName.replace(new RegExp(`\\b${oldClass}\\b`, 'g'), newClass)
    })
  }
  
  // Apply spacing migrations
  if (migrateSpacing) {
    Object.entries(migrateCSSClasses.spacing).forEach(([oldClass, newClass]) => {
      migratedClassName = migratedClassName.replace(new RegExp(`\\b${oldClass}\\b`, 'g'), newClass)
    })
  }
  
  // Apply shadow migrations
  if (migrateShadows) {
    Object.entries(migrateCSSClasses.shadows).forEach(([oldClass, newClass]) => {
      migratedClassName = migratedClassName.replace(new RegExp(`\\b${oldClass}\\b`, 'g'), newClass)
    })
  }
  
  return migratedClassName.trim()
}

/**
 * Generate migration report for a component tree
 */
export interface MigrationReport {
  componentsAnalyzed: number
  lowComplexity: number
  mediumComplexity: number
  highComplexity: number
  suggestedStrategy: keyof typeof migrationStrategies
  estimatedEffort: 'minimal' | 'moderate' | 'significant'
  recommendations: string[]
}

export const generateMigrationReport = (components: Array<{ name: string, props: any }>): MigrationReport => {
  let lowComplexity = 0
  let mediumComplexity = 0
  let highComplexity = 0
  const recommendations: string[] = []
  
  components.forEach(({ name, props }) => {
    const analysis = analyzeMigrationComplexity(props)
    
    switch (analysis.complexity) {
      case 'low':
        lowComplexity++
        break
      case 'medium':
        mediumComplexity++
        break
      case 'high':
        highComplexity++
        recommendations.push(`${name}: ${analysis.reasons.join(', ')}`)
        break
    }
  })
  
  // Determine overall strategy
  let suggestedStrategy: keyof typeof migrationStrategies = 'progressive'
  let estimatedEffort: MigrationReport['estimatedEffort'] = 'moderate'
  
  if (highComplexity === 0 && mediumComplexity < lowComplexity) {
    suggestedStrategy = 'radical'
    estimatedEffort = 'minimal'
  } else if (highComplexity > components.length * 0.3) {
    suggestedStrategy = 'conservative'
    estimatedEffort = 'significant'
  }
  
  // General recommendations
  if (estimatedEffort === 'minimal') {
    recommendations.unshift('✅ Great news! Your codebase is ready for radical v3 migration')
  } else if (estimatedEffort === 'moderate') {
    recommendations.unshift('⚠️ Consider progressive migration with testing at each step')
  } else {
    recommendations.unshift('🔄 Conservative approach recommended with careful testing')
  }
  
  return {
    componentsAnalyzed: components.length,
    lowComplexity,
    mediumComplexity,
    highComplexity,
    suggestedStrategy,
    estimatedEffort,
    recommendations
  }
}

/**
 * Runtime migration helper for development
 */
export const createMigrationLogger = (enabled = process.env.NODE_ENV === 'development') => {
  if (!enabled) {
    return {
      logMigration: () => {},
      logWarning: () => {},
      logSuccess: () => {},
    }
  }
  
  return {
    logMigration: (componentName: string, fromVersion: string, toVersion: string) => {
      console.log(
        `%c[BIRHAUS Migration]%c ${componentName} migrated from ${fromVersion} to ${toVersion}`,
        'background: #1F6FEB; color: white; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
        'color: #1F6FEB; font-weight: normal;'
      )
    },
    
    logWarning: (componentName: string, warning: string) => {
      console.warn(
        `%c[BIRHAUS Migration Warning]%c ${componentName}: ${warning}`,
        'background: #F59E0B; color: white; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
        'color: #F59E0B; font-weight: normal;'
      )
    },
    
    logSuccess: (componentName: string, message: string) => {
      console.log(
        `%c[BIRHAUS Migration Success]%c ${componentName}: ${message}`,
        'background: #10B981; color: white; padding: 2px 6px; border-radius: 3px; font-weight: bold;',
        'color: #10B981; font-weight: normal;'
      )
    },
  }
}